import { AntDesign } from "@expo/vector-icons";
import { capitalCase } from "change-case";
import { BlurView } from "expo-blur";
import { shareAsync } from "expo-sharing";
import React, { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  View,
  Modal,
  Pressable,
  Text,
  Platform,
  ScrollView,
} from "react-native";
import ViewShot from "react-native-view-shot";

import IzziWalletLogo from "@/assets/izziwalletlogo.svg";
import i18n from "@/locales/i18n";
import {
  generalKeysToIgnore,
  internalTransferKeysToIgnore,
} from "@/utils/management/Utils";
import { formatToFloat } from "@/utils/transactions/FormatTransactionValue";

interface DepositModalProps {
  visible: boolean;
  details: any;
  onClick?: (state: boolean) => void;
  onClose: () => void;
  generateFile?: boolean;
  isFileGenerated?: (state: boolean) => void;
}

export default function AdminReceiptModal(props: DepositModalProps) {
  const { t } = useTranslation();
  const ref = useRef<ViewShot>(null);

  const status = {
    AUTHORIZED: t("common:status.authorized"),
    PENDING: t("common:status.pending"),
    POSTED: t("common:status.posted"),
    CANCELLED: t("common:status.cancelled"),
    REJECTED: t("common:status.rejected"),
  };

  const printToFile = useCallback(async () => {
    if (ref.current) {
      try {
        const uri = await ref.current.capture();
        await shareScreenshot(uri);
      } catch (error) {
        console.error("Error capturing or sharing image:", error);
      }
    }
  }, []);

  const shareScreenshot = async (uri: string) => {
    try {
      if (Platform.OS === "web") {
        const downloadLink = document.createElement("a");
        downloadLink.href = uri;
        downloadLink.target = "_blank";
        downloadLink.download = "receipt.jpg";
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      } else {
        await shareAsync(uri);
      }
    } catch (error) {
      console.log("Error sharing screenshot:", error);
    }
  };

  const handleClose = () => {
    props.onClose();
  };

  const renderObject = (obj: object, excludeKeys: string[]) => {
    const displayedKeys = new Set<string>();

    const iterateObject = (obj: object, parentKey = "") => {
      return Object.entries(obj).flatMap(([key, value]) => {
        if (excludeKeys.includes(key) || displayedKeys.has(key)) {
          return [];
        }
        displayedKeys.add(key);
        if (value !== null && typeof value === "object") {
          return iterateObject(value, `${parentKey}${key}.`);
        } else if (value !== null) {
          return (
            <View
              className="flex-row w-full justify-between"
              key={`${parentKey}${key}`}
            >
              <Text className="text-sm font-light">{`${
                parentKey === "sourceAccountId" ||
                parentKey === "destinationAccountId"
                  ? `Account - ${capitalCase(key)}:`
                  : `${capitalCase(key)}`
              }:`}</Text>
              <Text className="text-sm">{String(value)}</Text>
            </View>
          );
        }
        return [];
      });
    };

    return <>{iterateObject(obj)}</>;
  };

  return (
    <Modal visible={props.visible} transparent animationType="fade">
      <BlurView
        intensity={20}
        className="flex-1 justify-center items-center m-5"
      >
        <ScrollView
          style={{
            width: "35%",
            backgroundColor: "white",
            padding: 16,
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.25,
            shadowRadius: 3.84,
            elevation: 5,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: "#F9F9F9",
          }}
        >
          <Pressable
            className="z-50 absolute right-3 top-3"
            onPress={handleClose}
          >
            <AntDesign name="close" size={24} color="black" />
          </Pressable>
          <ViewShot
            style={{ width: "100%", alignItems: "center", padding: 2 }}
            ref={ref}
            options={{ format: "jpg", quality: 1 }}
          >
            <View className="w-full h-fit items-center">
              <IzziWalletLogo width={140} height={40} />
            </View>
            <View className="w-full items-center m-3">
              <Text className="text-black1 text-2xl text-center font-medium">
                {t("common:modal.details.title")}
              </Text>
            </View>
            <View className="w-full border-b border-midgray border-dashed items-center p-2">
              <View className="flex-row w-full justify-between">
                <Text className="text-sm font-thin">
                  {`${t("management/transactions:receipt.transactionId")}:`}
                </Text>
                <Text className="text-sm">
                  {` ${props.details.id ? props.details.id : "N/A"}`}
                </Text>
              </View>
              {props.details.amountToDebit && (
                <View className="flex-row w-full justify-between">
                  <Text className="text-sm font-thin">
                    {`${t("management/transactions:receipt.value")}:`}
                  </Text>
                  <Text className="text-sm">{`${new Intl.NumberFormat(
                    i18n.language,
                    {
                      style: "currency",
                      currency: "USD",
                    }
                  ).format(formatToFloat(props.details.amountToDebit))}`}</Text>
                </View>
              )}
              {props.details.value && (
                <View className="flex-row w-full justify-between">
                  <Text className="text-sm font-thin">
                    {`${t("management/transactions:receipt.value")}: `}
                  </Text>
                  <Text className="text-sm">{`${props.details.value}`}</Text>
                </View>
              )}
              {props.details.rail && (
                <View className="flex-row w-full justify-between">
                  <Text className="text-sm font-thin">
                    {`${t("management/transactions:receipt.rail")}:`}
                  </Text>
                  <Text className="text-sm">{` ${props.details.rail}`}</Text>
                </View>
              )}
              <View className="flex-row w-full justify-between">
                <Text className="text-sm font-thin">
                  {`${t("home/home:details.status")}:`}
                </Text>
                <Text className="text-sm">
                  {` ${
                    status[
                      props.details.transactionStatus || props.details.status
                    ] || "N/A"
                  }`}
                </Text>
              </View>
              {props.details.exchangeRate && (
                <View className="flex-row w-full justify-between">
                  <Text className="text-sm font-thin">
                    {`${t("management/transactions:receipt.exchangeRate")}:`}
                  </Text>
                  <Text className="text-sm">{` ${props.details.exchangeRate}`}</Text>
                </View>
              )}
              <View className="flex-row w-full justify-between">
                <Text className="text-sm font-thin">
                  {`${t("home/home:details.date")}:`}
                </Text>
                <Text className="text-sm">
                  {` ${
                    props.details.createdAt
                      ? new Date(props.details.createdAt).toLocaleString(
                          i18n.language,
                          {
                            month: "long",
                            day: "numeric",
                            hour: "numeric",
                            minute: "numeric",
                            hour12: true,
                          }
                        )
                      : "N/A"
                  }`}
                </Text>
              </View>
            </View>
            {props.details.transactionType === "INTERNAL_TRANSFER" && (
              <View className="w-full border-b border-midgray border-dashed items-center p-2">
                {renderObject(props.details, [
                  ...generalKeysToIgnore,
                  ...internalTransferKeysToIgnore,
                ])}
              </View>
            )}
            {props.details.originator && (
              <View className="w-full border-b border-midgray border-dashed items-center p-2">
                <View className="w-full items-center my-2">
                  <Text className="text-base text-center">
                    {t("management/transactions:receipt.titles.originator")}
                  </Text>
                </View>
                {renderObject(props.details.originator, [
                  ...generalKeysToIgnore,
                  "id",
                  "assetTypeId",
                ])}
              </View>
            )}
            {props.details.account && (
              <View className="w-full border-b border-midgray border-dashed items-center p-2">
                <View className="w-full items-center my-2">
                  <Text className="text-base text-center">
                    {t("management/transactions:receipt.titles.account")}
                  </Text>
                </View>
                {renderObject(props.details.account, generalKeysToIgnore)}
              </View>
            )}
            {props.details.destinationCounterpartyId && (
              <View className="w-full border-b border-midgray border-dashed items-center p-2">
                <View className="w-full items-center my-2">
                  <Text className="text-base text-center">
                    {t(
                      "management/transactions:receipt.titles.beneficiaryinformation"
                    )}
                  </Text>
                </View>
                {renderObject(props.details.destinationCounterpartyId.profile, [
                  ...generalKeysToIgnore,
                  "id",
                  "profileType",
                  "rails",
                  "createdAt",
                ])}
              </View>
            )}
            {props.details.destinationCounterpartyId && (
              <View className="w-full border-b border-midgray border-dashed items-center p-2">
                <View className="w-full items-center my-2">
                  <Text className="text-base text-center">
                    {t(
                      "management/transactions:receipt.titles.destinationaccount"
                    )}
                  </Text>
                </View>
                {renderObject(
                  props.details.destinationCounterpartyId.accountInformation,
                  [...generalKeysToIgnore, "id", "assetTypeId", "createdAt"]
                )}
              </View>
            )}
            {props.details.sourceDetails && (
              <View className="w-full border-b border-midgray border-dashed items-center p-2">
                <View className="w-full items-center my-2">
                  <Text className="text-base text-center">
                    {t("management/transactions:receipt.titles.sourceaccount")}
                  </Text>
                </View>
                {renderObject(props.details.sourceDetails, [
                  ...generalKeysToIgnore,
                  "productId",
                  "productType",
                  "feeStructure",
                  "currentBalance",
                  "availableBalance",
                  "status",
                  "createdAt",
                ])}
              </View>
            )}
            {props.details.destinationDetails && (
              <View className="w-full border-b border-midgray border-dashed items-center p-2">
                <View className="w-full items-center my-2">
                  <Text className="text-base text-center">
                    {t(
                      "management/transactions:receipt.titles.destinationaccount"
                    )}
                  </Text>
                </View>
                {renderObject(props.details.destinationDetails, [
                  ...generalKeysToIgnore,
                  "productId",
                  "productType",
                  "feeStructure",
                  "currentBalance",
                  "availableBalance",
                  "status",
                ])}
              </View>
            )}
            {props.details.fee && (
              <View className="w-full border-b border-midgray border-dashed items-center p-2">
                <View className="w-full items-center my-2">
                  <Text className="text-base text-center">
                    {t("management/transactions:receipt.titles.fees")}
                  </Text>
                </View>
                {renderObject(props.details.fee, [
                  ...generalKeysToIgnore,
                  "id",
                  "layer2TransactionId",
                  "createdAt",
                ])}
              </View>
            )}
            <View className="w-full items-center">
              <Text className="text-midgray text-sm mt-1">
                ©2024 Izzi Wallet. All rights reserved.
              </Text>
            </View>
          </ViewShot>
          <View className="flex-grow w-full p-1 items-center justify-end">
            <Pressable
              className="flex-row items-center mt-2"
              onPress={printToFile}
            >
              <AntDesign name="download" size={16} color="#919191" />
              <Text className="mx-2 text-darkergray text-sm">
                {t("common:placeholder.download")}
              </Text>
            </Pressable>
          </View>
        </ScrollView>
      </BlurView>
    </Modal>
  );
}
